import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { ComplianceFooter, ComplianceCallout, WarningBanner } from "@/components/Compliance";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Award, CheckCircle2, Lock, ArrowRight, X, Sparkles, Flame } from "lucide-react";

function weekIdx(dateStr) {
  const t = Date.parse(dateStr);
  if (Number.isNaN(t)) return null;
  return Math.floor(t / (7 * 86400000));
}

function computeStreak(weeks) {
  const now = Math.floor(Date.now() / (7 * 86400000));
  let c = weeks.has(now) ? now : weeks.has(now - 1) ? now - 1 : null;
  if (c === null) return 0;
  let s = 0;
  while (weeks.has(c)) {
    s += 1;
    c -= 1;
  }
  return s;
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState({ course: null, modules: [] });
  const [progress, setProgress] = useState({ progress: [], quiz_attempts: [], badges: [] });
  const [allBadges, setAllBadges] = useState([]);
  const [upsells, setUpsells] = useState([]);
  const [onboarding, setOnboarding] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(true);
  const [loading, setLoading] = useState(false);

  const hasAccess = user?.has_access || user?.role === "admin";

  useEffect(() => {
    api.get("/course").then((r) => setCourse(r.data)).catch(() => {});
    api.get("/upsells", { params: { placement: "dashboard" } }).then((r) => setUpsells(r.data)).catch(() => {});
    api.get("/compliance", { params: { location: "onboarding" } }).then((r) => setOnboarding(r.data?.[0] || null)).catch(() => {});
    api.get("/badges").then((r) => setAllBadges(r.data)).catch(() => {});
    if (hasAccess) api.get("/me/progress").then((r) => setProgress(r.data)).catch(() => {});
  }, [hasAccess]);

  const moduleProgress = useMemo(() => {
    const map = {};
    const byModule = {};
    (progress.progress || []).forEach((p) => {
      byModule[p.module_id] = byModule[p.module_id] || { lesson: 0, checklist: 0, module: null };
      if (p.item_type === "module" && p.completed) byModule[p.module_id].module = p;
    });
    course.modules.forEach((m) => {
      const lessonsDone = (progress.progress || []).filter((p) => p.module_id === m.id && p.item_type === "lesson" && p.completed).length;
      const checklistDone = (progress.progress || []).filter((p) => p.module_id === m.id && p.item_type === "checklist" && p.completed).length;
      const total = (m.lesson_count || 0) + (m.checklist_count || 0);
      const done = lessonsDone + checklistDone;
      const modComplete = !!byModule[m.id]?.module;
      const pct = total ? Math.round((done / total) * 100) : 0;
      map[m.id] = { pct: modComplete ? 100 : pct, modComplete, skipped: byModule[m.id]?.module?.skipped_required || [], started: done > 0 };
    });
    return map;
  }, [progress, course.modules]);

  const warnings = useMemo(() => {
    const msgs = [];
    course.modules.forEach((m) => {
      const mp = moduleProgress[m.id];
      if (mp?.modComplete && mp.skipped?.length) {
        msgs.push(`Module ${m.module_number}: you marked it complete but skipped ${mp.skipped.length} required step${mp.skipped.length > 1 ? "s" : ""}. You can continue, but completing them improves your chances of launching successfully.`);
      }
    });
    return msgs;
  }, [course.modules, moduleProgress]);

  const earnedBadgeIds = new Set((progress.badges || []).map((b) => b.badge_id));
  const completedCount = Object.values(moduleProgress).filter((p) => p.modComplete).length;
  const overallPct = course.modules.length ? Math.round((completedCount / course.modules.length) * 100) : 0;
  const nextModule = course.modules.find((m) => !moduleProgress[m.id]?.modComplete);

  const streak = useMemo(() => {
    const weeks = new Set();
    (progress.progress || []).forEach((p) => {
      if (p.completed && p.completed_at) {
        const w = weekIdx(p.completed_at);
        if (w !== null) weeks.add(w);
      }
    });
    (progress.quiz_attempts || []).forEach((a) => {
      const w = weekIdx(a.created_at);
      if (w !== null) weeks.add(w);
    });
    return computeStreak(weeks);
  }, [progress]);

  const buy = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout.");
      setLoading(false);
    }
  };

  const stages = ["Foundations", "Find and Approve", "Build and Launch", "Run and Grow"];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900" data-testid="dashboard-heading">
              Welcome back, {user?.name?.split(" ")[0] || "there"}
            </h1>
            <p className="text-slate-600 mt-1">Work through all 15 modules at your own pace.</p>
            {hasAccess && nextModule && (
              <Button className="mt-3 bg-slate-900 hover:bg-slate-800" onClick={() => navigate(`/module/${nextModule.id}`)} data-testid="continue-learning-button">
                Continue learning · Module {nextModule.module_number} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
          <div className="flex items-center gap-6">
            {hasAccess && (
              <div className="text-right" data-testid="streak-display">
                <p className="text-sm text-slate-500">Learning streak</p>
                <p className={`font-display text-2xl font-bold flex items-center gap-1.5 justify-end ${streak > 0 ? "text-amber-600" : "text-slate-400"}`}>
                  <Flame className="w-5 h-5" /> {streak} {streak === 1 ? "week" : "weeks"}
                </p>
              </div>
            )}
            <div className="text-right">
              <p className="text-sm text-slate-500">Course progress</p>
              <p className="font-display text-2xl font-bold text-slate-900" data-testid="overall-progress">{overallPct}%</p>
            </div>
          </div>
        </div>

        {!hasAccess && (
          <div className="mt-6 rounded-2xl border border-amber-300 bg-amber-50 p-6 flex flex-wrap items-center justify-between gap-4" data-testid="locked-banner">
            <div>
              <h2 className="font-display text-lg font-bold text-amber-900">Unlock the full course</h2>
              <p className="text-sm text-amber-800 mt-1">Your account is ready. Purchase one-time access to open all 15 modules.</p>
            </div>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={buy} disabled={loading} data-testid="dashboard-buy-button">
              {loading ? "Starting…" : "Get full access"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {hasAccess && showOnboarding && onboarding && (
          <div className="mt-6 relative">
            <ComplianceCallout block={onboarding} testId="onboarding-compliance-block" />
            <button className="absolute top-3 right-3 text-slate-400 hover:text-slate-600" onClick={() => setShowOnboarding(false)} data-testid="dismiss-onboarding"><X className="w-4 h-4" /></button>
          </div>
        )}

        {hasAccess && warnings.length > 0 && (
          <div className="mt-6">
            <WarningBanner messages={warnings} />
          </div>
        )}

        {hasAccess && (
          <div className="mt-6">
            <Progress value={overallPct} className="h-2" data-testid="course-progress-bar" />
          </div>
        )}

        {/* Modules */}
        <div className="mt-8 space-y-10">
          {stages.map((stage) => {
            const mods = course.modules.filter((m) => m.stage === stage);
            if (!mods.length) return null;
            return (
              <div key={stage}>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-700 mb-3">{stage}</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                  {mods.map((m) => {
                    const mp = moduleProgress[m.id] || { pct: 0 };
                    return (
                      <button
                        key={m.id}
                        onClick={() => (hasAccess ? navigate(`/module/${m.id}`) : buy())}
                        className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-slate-900 hover:shadow-md transition-all group relative"
                        data-testid={`dashboard-module-card-${m.module_number}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono text-xs font-semibold text-slate-400">{String(m.module_number).padStart(2, "0")}</span>
                          {!hasAccess ? (
                            <Lock className="w-4 h-4 text-slate-300" />
                          ) : mp.modComplete ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-600" data-testid={`module-complete-${m.module_number}`} />
                          ) : mp.skipped?.length ? (
                            <span className="w-2 h-2 rounded-full bg-amber-500" title="Skipped required steps" />
                          ) : null}
                        </div>
                        <h3 className="font-display font-semibold text-slate-900 leading-snug text-sm min-h-[40px]">{m.title}</h3>
                        {hasAccess && (
                          <div className="mt-3">
                            <Progress value={mp.pct} className="h-1.5" />
                            <p className="text-xs text-slate-400 mt-1.5">{mp.modComplete ? "Completed" : mp.started ? `${mp.pct}% complete` : `${m.estimated_minutes || 0} min · not started`}</p>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Badges */}
        {hasAccess && (
          <div className="mt-12">
            <h2 className="font-display text-xl font-bold text-slate-900 mb-4">Your badges</h2>
            <div className="flex flex-wrap gap-3">
              {allBadges.filter((b) => b.trigger_type === "course_complete").concat(allBadges.filter((b) => b.trigger_type === "module_complete")).map((b) => {
                const earned = earnedBadgeIds.has(b.id);
                return (
                  <div key={b.id} className={`rounded-xl border p-3 flex items-center gap-2.5 ${earned ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white opacity-60"}`} data-testid={`badge-item-${b.id}`}>
                    <Award className={`w-5 h-5 ${earned ? "text-emerald-600" : "text-slate-300"}`} />
                    <span className={`text-sm font-medium ${earned ? "text-emerald-900" : "text-slate-400"}`}>{b.title}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Certificate CTA */}
        {hasAccess && completedCount === course.modules.length && course.modules.length > 0 && (
          <div className="mt-12 rounded-2xl border border-emerald-300 bg-emerald-50 p-6 flex flex-wrap items-center justify-between gap-4" data-testid="certificate-cta">
            <div className="flex items-center gap-3">
              <Award className="w-8 h-8 text-emerald-600" />
              <div>
                <h2 className="font-display text-lg font-bold text-emerald-900">You finished all 15 modules!</h2>
                <p className="text-sm text-emerald-800">Download your certificate of completion.</p>
              </div>
            </div>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => navigate("/certificate")} data-testid="view-certificate-button">
              View certificate
            </Button>
          </div>
        )}

        {/* Upsells */}
        {upsells.length > 0 && (
          <div className="mt-12">
            <h2 className="font-display text-xl font-bold text-slate-900 mb-4">Go further</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {upsells.map((u) => (
                <div key={u.id} className="rounded-xl border border-slate-200 bg-white p-5 flex flex-col" data-testid={`dashboard-upsell-${u.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-amber-600" />
                    <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 border-0">{u.price_text}</Badge>
                  </div>
                  <h3 className="font-display font-semibold text-slate-900">{u.title}</h3>
                  <p className="text-sm text-slate-600 mt-1 flex-1">{u.description}</p>
                  <a href={u.cta_url} target="_blank" rel="noreferrer" className="mt-4">
                    <Button variant="outline" className="w-full" data-testid={`upsell-cta-${u.id}`}>{u.cta_text}</Button>
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
      <ComplianceFooter />
    </div>
  );
}
